import React, { Fragment } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Select, { StylesConfig } from "react-select";
import cx from "classnames";
import { pages, sections } from "../../constants";
import Button from "../Button";
import sgRooms from "../../pages/RoomsOverview/rooms.json";
import baliRooms from "../../pages/RoomsOverview/rooms-bali.json";
import klRooms from "../../pages/RoomsOverview/rooms-kl.json";
import suitesRooms from "../../pages/RoomsOverview/rooms-suites.json";
import styles from "./styles.module.sass";

interface Item {
  slug: string;
  name: string;
  value?: string;
  label?: string;
  className?: string;
}

interface RoomGroup {
  label: string;
  options: Item[];
}

interface HorizontalNavProps {
  type?: "" | "local";
}

const HorizontalNav: React.FC<HorizontalNavProps> = ({ type = "" }) => {
  const navigate = useNavigate();
  const params = useParams<{ country?: string; type?: string }>();
  const location = useLocation();

  // pick global pages vs local sections
  const items = type === "local" ? sections : pages;
  const base = type === "local" && params.country ? `/${params.country}` : "";

  // handler for dropdown change
  const onChange = (option: Item | null) => {
    if (option) navigate(`${base}/${option.value}`);
  };

  // build dropdown items from pages/sections
  let dropdownItems: (Item | RoomGroup)[] = items.map((item) => ({
    ...item,
    value: item.slug,
    label: item.name,
    className: styles.dropdownOption,
  }));

  // choose the right rooms JSON
  let rooms = sgRooms;
  if (params.country === "bali") rooms = baliRooms;
  else if (params.country === "kuala-lumpur") rooms = klRooms;
  else if (params.country === "suites") rooms = suitesRooms;

  // insert room groups after first section
  if (type === "local") {
    const roomGroups: RoomGroup[] = rooms.map((group) => ({
      label: group.category,
      options: group.rooms.map((room) => ({
        slug: `rooms/${room.slug}`,
        name: room.name,
        value: `rooms/${room.slug}`,
        label: room.name,
        className: styles.dropdownOption, // same style as top-level
      })),
    }));
    dropdownItems.splice(1, 0, ...roomGroups);
  }

  // split path into segments for matching
  const segments = location.pathname.split("/").filter(Boolean);

  // determine which item to select by default
  const defaultSelected = (): Item | null => {
    if (segments.includes("rest")) {
      const restItem = (dropdownItems as Item[]).find(
        (item) => !("options" in item) && item.value === "rest"
      );
      if (restItem) return restItem;
    }

    for (const group of dropdownItems) {
      if ("options" in group) {
        for (const room of group.options) {
          if (location.pathname.includes(room.value || "")) {
            return room;
          }
        }
      }
    }

    const picked = (dropdownItems as Item[]).find(
      (item) => !("options" in item) && segments.includes(item.value || "")
    );
    return picked || ((dropdownItems as Item[])[0] as Item);
  };

  // custom styles for react-select
  const customStyles: StylesConfig<Item, false> = {
    option: (provided, state) => {
      const isRoom =
        typeof state.data.value === "string" &&
        state.data.value.startsWith("rooms/");

      const isSelectedRoom =
        typeof state.data.value === "string" &&
        state.data.value.endsWith(`${params.type}`);

      return {
        ...provided,
        ...(state.data.className ? { className: state.data.className } : {}),
        color: "#000",
        backgroundColor: "transparent",
        "&:hover": { backgroundColor: "transparent" },
        borderBottom: isRoom ? "none" : "1px solid #e5e5e5",
        padding: isRoom ? "8px 12px 8px 24px" : "8px 12px",
        fontWeight: isSelectedRoom ? "bold" : "normal",
        fontSize: isRoom ? "1.3rem" : "1.6rem",
      };
    },
    control: (provided) => ({
      ...provided,
      minWidth: "200px",
      border: "none",
      boxShadow: "none",
      "&:hover": { border: "none" },
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "transparent",
      borderLeft: "1px #000 solid",
      borderBottom: "1px #000 solid",
      content: '" "',
      display: "block",
      marginTop: "-ceil(2.5)",
      position: "absolute",
      right: "10px",
      top: "1rem",
      width: "1rem",
      height: "1rem",
      transform: "rotate(-45deg)",
      "&:hover": { color: "transparent" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (provided) => ({
      ...provided,
      overflow: "visible",
      border: "none",
      boxShadow: "none",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "none",
    }),
    groupHeading: (base) => ({
      ...base,
      // backgroundColor: "red",
      color: "#000",
      fontSize: "1.6rem",
      padding: "0.5rem 1rem",
      textTransform: "uppercase",
    }),
  };

  const classNames = cx(styles.dropdown, {
    [styles.dropdownLocal]: type === "local",
  });

  return (
    <Fragment>
      <div className={styles.nav}>
        {items.map((item) => (
          <Button
            key={item.slug}
            className={styles.navItem}
            buttonVariant="frameless"
            active={segments.includes(item.slug)}
            disabled={
              segments.includes(item.slug) && !segments.includes("rooms")
            }
            size="small"
            to={`${base}/${item.slug}`}
          >
            {item.name}
          </Button>
        ))}
      </div>

      <Select
        className={classNames}
        classNamePrefix="react-select"
        value={defaultSelected()}
        options={dropdownItems}
        placeholder="Select an option"
        onChange={onChange}
        styles={customStyles}
        isClearable={false}
        hideSelectedOptions={false}
      />
    </Fragment>
  );
};

export default HorizontalNav;
